import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Wrapper } from '@pkg/components';
import {
    DATE_DEFAULT,
    type DateValues,
    DurationUnit,
    type DurationUnits,
    GameDatesDisplay,
    Input,
    LabelledInput,
    nowFormatted,
    useGetDurationDefaults,
} from '@pkg/components/Sections/Admin';
import { DescriptionField, StyledLabel } from '@pkg/components/Sections/Admin/components/Form/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type Competition } from '@app/client/common/structs';
import {
    addTime,
    handleDateValidation,
    handleInputDate,
    handleInputValue,
    handleValueValidation,
    useFormErrors,
} from '@app/helpers';

interface GameDatesProps {
    onUpdate: (dateValues: DateValues) => void;
    gameData?: Competition;
    isDuplicate?: boolean;
}

export const displayFormat = '(UTC) DD MMM YYYY - HH:mm';
export const displayFormatNow = 'DD MMM YYYY - HH:mm';

export const GameDates = ({ onUpdate, gameData, isDuplicate = false }: GameDatesProps) => {
    const { t } = useTranslation('admin_fantasy');
    const durationDefaults = useGetDurationDefaults(gameData, isDuplicate);
    const [dateData, setDateData] = useState(DATE_DEFAULT);
    const [formData, setFormData] = useState(durationDefaults.formDefaults);
    const [durationUnits, setDurationUnits] = useState<DurationUnits>(durationDefaults.durationDefaults);

    useEffect(() => {
        setFormData(durationDefaults.formDefaults);
        // eslint-disable-next-line
    }, [gameData]);

    const { formErrors, handleError } = useFormErrors({ fieldNames: ['submission_starts_at', 'submissions', 'game'] });

    function handleValueChanges(name, value, newDurationUnit?) {
        let newValues;
        const toggledUnits = {
            submissions: name === 'submissions' && newDurationUnit ? newDurationUnit : durationUnits.submissions,
            game: name === 'game' && newDurationUnit ? newDurationUnit : durationUnits.game,
        };
        const newDate = (initialDate, increment, durationUnit) => addTime(initialDate, increment, durationUnit);

        switch (name) {
            case 'submission_starts_at': {
                const newSubValue = newDate(value, formData.submissions, toggledUnits.submissions);
                const newGameValue = newDate(newSubValue, formData.game, toggledUnits.game);
                newValues = { submission_starts_at: value, submissions: newSubValue, game: newGameValue };
                break;
            }
            case 'submissions': {
                const newSubValue = newDate(dateData.submission_starts_at, value, toggledUnits.submissions);
                const newGameValue = newDate(newSubValue, formData.game, toggledUnits.game);
                newValues = {
                    submissions: newSubValue,
                    game: newGameValue,
                };

                setFormData((fd) => ({ ...fd, [name]: value }));
                break;
            }
            case 'game': {
                newValues = { game: newDate(dateData.submissions, value, toggledUnits.game) };
                setFormData((fd) => ({ ...fd, [name]: value }));
                break;
            }
        }

        setDateData((dd) => ({ ...dd, ...newValues }));
        const updateVals = { ...dateData, ...newValues };
        onUpdate(updateVals);
    }

    function handleInputChanges(e) {
        const { name, type } = e.target;
        const value = handleInputValue(e);
        const error = type === 'datetime-local' ? handleDateValidation(e) : handleValueValidation(e);
        handleError(name, error);
        handleValueChanges(name, value);
    }

    function handleDurationSwitch(name, currentUnit: DurationUnit) {
        const units = Object.values(DurationUnit);
        const currentIndex = units.indexOf(currentUnit);
        const newUnit = units[currentIndex < units.length - 1 ? currentIndex + 1 : 0];
        setDurationUnits((curr) => ({ ...curr, [name]: newUnit }));
        handleValueChanges(name, formData[name], newUnit);
    }

    return (
        <>
            {[
                <Wrapper width="45%" margin="10px 0" key="starts at">
                    <LabelledInput
                        required
                        onChange={handleInputChanges}
                        name="submission_starts_at"
                        id="submission_starts_at"
                        value={handleInputDate(dateData.submission_starts_at)}
                        label={t(`create_label_submission_starts_at`)}
                        type="datetime-local"
                        description="This is when the game accepts new entries."
                        min={nowFormatted}
                        errorMessage={formErrors['submission_starts_at']}
                    />
                </Wrapper>,
                <Wrapper width="45%" key="joining window">
                    <Wrapper direction="row" justifyContent="space-between" margin="10px 0">
                        <StyledLabel htmlFor="submissions">Game joining window *</StyledLabel>
                        <Button
                            style={{ minHeight: 'unset' }}
                            type="button"
                            color="transparent"
                            size="xsmall"
                            onClick={() => handleDurationSwitch('submissions', durationUnits.submissions)}
                        >
                            Change Duration type (days/hours)
                        </Button>
                    </Wrapper>
                    <Input
                        required
                        labelInside={durationUnits.submissions}
                        labelPosition="end"
                        onChange={handleInputChanges}
                        name="submissions"
                        id="submissions"
                        value={formData.submissions}
                        placeholder={t('prizes_placeholder_rank')}
                        type="number"
                        errorMessage={formErrors['submissions']}
                        min={1}
                        max={1000}
                    />
                    <DescriptionField>
                        This is the window where new players can join the game by submitting at least one trade.
                    </DescriptionField>
                </Wrapper>,
                <Wrapper width="45%" key="game duration">
                    <Wrapper direction="row" justifyContent="space-between" margin="10px 0">
                        <StyledLabel htmlFor="submissions">Game trading duration *</StyledLabel>
                        <Button
                            type="button"
                            style={{ minHeight: 'unset' }}
                            color="transparent"
                            size="xsmall"
                            onClick={() => handleDurationSwitch('game', durationUnits.game)}
                        >
                            Change Duration type (days/hours)
                        </Button>
                    </Wrapper>
                    <Input
                        required
                        labelInside={durationUnits.game}
                        labelPosition="end"
                        onChange={handleInputChanges}
                        name="game"
                        errorMessage={formErrors['game']}
                        id="game"
                        value={formData.game}
                        placeholder={t('prizes_placeholder_rank')}
                        type="number"
                        min={1}
                        max={1000}
                    />
                    <DescriptionField>
                        This is the window where players can make unlimited trades but no new players can join this
                        game.
                    </DescriptionField>
                </Wrapper>,
                <Wrapper key="reference dates" direction="row" padding="10px 0" gap="30px" width="100%">
                    <GameDatesDisplay
                        start={dayjs(dateData.submission_starts_at).format(displayFormatNow)}
                        joinEnd={dayjs(dateData.submissions).format(displayFormatNow)}
                        end={dayjs(dateData.game).format(displayFormatNow)}
                    />
                </Wrapper>,
            ]}
        </>
    );
};
